import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, SlidersHorizontal } from "lucide-react";
import { MaskedLines, Fade, Reveal } from "@/components/motion";
import ProjectCard from "@/components/ProjectCard";
import EmptyState from "@/components/EmptyState";
import SearchBar from "@/components/SearchBar";
import { IMG, PROJECT_STATUSES } from "@/lib/site";
import { CITIES, getCity } from "@/data/cities";
import { searchProjects } from "@/data/projects";

const CONFIG = {
    all: {
        eyebrow: "Discover · All of India",
        title: "Every new project, beautifully presented.",
        image: IMG.hero_dusk,
        copy: "Residential and commercial showcases from verified developers — curated, never crowded.",
    },
    residential: {
        eyebrow: "Residential",
        title: "Homes that begin as showcases.",
        image: IMG.residential_tower,
        copy: "Newly launched, under-construction and ready-to-move residences — direct from the developers building them.",
    },
    commercial: {
        eyebrow: "Commercial",
        title: "Workspaces and retail, discovered at launch.",
        image: IMG.commercial_office,
        copy: "Grade-A offices, boutique workspaces and high-street retail — presented before the crowd arrives.",
    },
};

export default function Discover({ category = "all" }) {
    const cfg = CONFIG[category] || CONFIG.all;
    const [searchParams, setSearchParams] = useSearchParams();
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [city, setCity] = useState(searchParams.get("city") || "");
    const [status, setStatus] = useState(searchParams.get("status") || "");

    useEffect(() => {
        setQ(searchParams.get("q") || "");
        setCity(searchParams.get("city") || "");
        setStatus(searchParams.get("status") || "");
    }, [searchParams]);

    const apply = (next) => {
        const params = {};
        const merged = { q, city, status, ...next };
        if (merged.q) params.q = merged.q;
        if (merged.city) params.city = merged.city;
        if (merged.status) params.status = merged.status;
        setSearchParams(params);
    };

    const results = searchProjects({
        q,
        city,
        status,
        category: category === "all" ? "" : category,
    });
    const cityObj = city ? getCity(city) : null;

    return (
        <div data-testid={`discover-${category}-page`}>
            <section className="relative flex min-h-[62vh] items-end overflow-hidden">
                <img
                    src={cfg.image}
                    alt={cfg.eyebrow}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-obsidian/25" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
                    <Fade delay={0.1}>
                        <p className="eyebrow">{cfg.eyebrow}</p>
                    </Fade>
                    <h1 className="mt-5 font-serif text-4xl font-light leading-[1.06] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
                        <MaskedLines lines={[cfg.title]} />
                    </h1>
                    <Fade delay={0.7}>
                        <p className="mt-5 max-w-xl text-sm leading-relaxed text-mist sm:text-base">
                            {cfg.copy}
                        </p>
                    </Fade>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
                <Reveal>
                    <div
                        className="glass flex flex-col gap-3 rounded-3xl p-3 lg:flex-row lg:items-center lg:rounded-full"
                        data-testid="discover-filters"
                    >
                        <div className="flex-1">
                            <SearchBar
                                bare
                                inputTestId="discover-search-input"
                                placeholder="Search city, locality or project"
                            />
                        </div>
                        <div className="flex items-center gap-3 border-black/10 px-4 py-2 lg:border-l">
                            <MapPin className="h-4 w-4 shrink-0 text-gold" />
                            <select
                                value={city}
                                onChange={(e) => {
                                    setCity(e.target.value);
                                    apply({ city: e.target.value });
                                }}
                                data-testid="discover-city-filter"
                                className="w-full bg-transparent text-sm text-mist focus:outline-none [&>option]:bg-surface"
                            >
                                <option value="">All cities</option>
                                {CITIES.map((c) => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3 border-black/10 px-4 py-2 lg:border-l">
                            <SlidersHorizontal className="h-4 w-4 shrink-0 text-gold" />
                            <select
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    apply({ status: e.target.value });
                                }}
                                data-testid="discover-status-filter"
                                className="w-full bg-transparent text-sm text-mist focus:outline-none [&>option]:bg-surface"
                            >
                                <option value="">Any status</option>
                                {PROJECT_STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </Reveal>

                <div className="mt-12">
                    {results.length > 0 ? (
                        <div
                            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
                            data-testid="discover-results"
                        >
                            {results.map((p, i) => (
                                <ProjectCard key={p.slug} project={p} index={i} />
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            citySlug={cityObj?.slug}
                            category={category === "all" ? undefined : category}
                            nearby={cityObj?.nearby || []}
                        />
                    )}
                </div>
            </section>
        </div>
    );
}
